import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Switch from '@mui/material/Switch'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import Chip from '@mui/material/Chip'
import SaveIcon from '@mui/icons-material/Save'
import { getRdSettings, updateRdSettings, type RdSetting } from '../../services/authApi'

interface SettingsTabProps {
  rdId: string
}

export function SettingsTab({ rdId }: SettingsTabProps) {
  const [settings, setSettings] = useState<RdSetting[]>([])
  const [values, setValues] = useState<Record<string, string>>({})
  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  const load = useCallback(async () => {
    try {
      const data = await getRdSettings(rdId)
      setSettings(data)
      const v: Record<string, string> = {}
      for (const s of data) v[s.key] = s.value
      setValues(v)
      setDirty(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings')
    }
  }, [rdId])

  useEffect(() => { load() }, [load])

  function handleChange(key: string, value: string) {
    setValues(prev => ({ ...prev, [key]: value }))
    setDirty(true)
    setSaved(false)
  }

  async function handleSave() {
    try {
      const result = await updateRdSettings(rdId, values)
      if (result.errors.length > 0) {
        setError(result.errors.map(e => `${e.key}: ${e.error}`).join(', '))
      } else {
        setDirty(false)
        setSaved(true)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings')
    }
  }

  // Group settings by category
  const categories = new Map<string, RdSetting[]>()
  for (const s of settings) {
    const cat = s.category || 'general'
    if (!categories.has(cat)) categories.set(cat, [])
    categories.get(cat)!.push(s)
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>Settings saved</Alert>}

      {Array.from(categories.entries()).map(([category, catSettings]) => (
        <Paper key={category} sx={{ border: '1px solid', borderColor: 'divider', p: 3, mb: 2 }} elevation={0}>
          <Typography variant="overline" sx={{ mb: 2, display: 'block' }}>
            {category}
          </Typography>

          {catSettings.map((setting, i) => (
            <Box key={setting.key}>
              {i > 0 && <Divider sx={{ my: 2 }} />}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="h6">{setting.label}</Typography>
                    <Chip label={setting.valueType} size="small" variant="outlined" />
                  </Box>
                  <Typography variant="body2" color="text.secondary">{setting.description}</Typography>
                </Box>
                <Box sx={{ flexShrink: 0, minWidth: 280 }}>
                  <SettingInput
                    setting={setting}
                    value={values[setting.key] ?? setting.defaultValue}
                    onChange={v => handleChange(setting.key, v)}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Paper>
      ))}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={!dirty}>
          Save Settings
        </Button>
      </Box>
    </Box>
  )
}

function SettingInput({ setting, value, onChange }: {
  setting: RdSetting
  value: string
  onChange: (value: string) => void
}) {
  switch (setting.valueType) {
    case 'boolean':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">{value === 'true' ? 'Enabled' : 'Disabled'}</Typography>
          <Switch checked={value === 'true'} onChange={e => onChange(e.target.checked ? 'true' : 'false')} />
        </Box>
      )

    case 'enum': {
      let options: string[] = []
      if (setting.allowedValues) {
        try { options = JSON.parse(setting.allowedValues) } catch { /* ignore */ }
      }
      return (
        <Select fullWidth size="small" value={value} onChange={e => onChange(e.target.value)}>
          {options.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
        </Select>
      )
    }

    case 'json':
      return (
        <TextField
          fullWidth size="small" value={value}
          onChange={e => onChange(e.target.value)}
          placeholder='["example.com"]'
          multiline={value.length > 40}
        />
      )

    case 'string':
    default:
      return (
        <TextField
          fullWidth size="small" value={value}
          onChange={e => onChange(e.target.value)}
        />
      )
  }
}
